import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

/**
 * InquiryShell — the full-page layout for every step of the consultation flow.
 *
 * Per the client's direction this experience is BRIGHT (not the site's dark
 * canvas): a clean off-white stage with a sophisticated "fractured glass"
 * motion background (frosted shards drifting over soft teal light). All accent
 * colour is TEAL here — achieved by scoping a light token palette on the
 * `.pg-inquiry` root, so every child (`var(--pg-*)`) inherits the light+teal
 * theme automatically.
 */
export const InquiryShell = ({ leftRail, children, dataTestId = "inquiry-shell" }) => {
    return (
        <div className="pg-inquiry relative min-h-screen" data-testid={dataTestId}>
            {/* Fractured glass-morphism motion background */}
            <div className="pg-inquiry__bg" aria-hidden="true">
                <span className="pg-inquiry__blob pg-inquiry__blob--a" />
                <span className="pg-inquiry__blob pg-inquiry__blob--b" />
                <span className="pg-inquiry__blob pg-inquiry__blob--c" />
                <span className="pg-inquiry__shard pg-inquiry__shard--1" />
                <span className="pg-inquiry__shard pg-inquiry__shard--2" />
                <span className="pg-inquiry__shard pg-inquiry__shard--3" />
                <span className="pg-inquiry__shard pg-inquiry__shard--4" />
                <span className="pg-inquiry__shard pg-inquiry__shard--5" />
                <span className="pg-inquiry__shard pg-inquiry__shard--6" />
            </div>

            {/* Top bar — intentionally NOT sticky: it only shows at the absolute
                top of the page. Once the user scrolls, it slides away and the
                guided-journey progress rail becomes the sticky glass bar. */}
            <header className="pg-inquiry__topbar relative z-20 flex items-center justify-between px-5 py-4 sm:px-8">
                <Link
                    to="/"
                    data-testid="inquiry-back-home"
                    className="pg-inquiry__backhome inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to site
                </Link>
                <span className="pg-inquiry__brand inline-flex items-center gap-2" data-testid="inquiry-brand-chip">
                    <span className="pg-inquiry__brand-dot" aria-hidden="true" />
                    <span className="pg-inquiry__brand-text">Pranto.Gfx — Consultation</span>
                </span>
            </header>

            {/* Body — vertical stepper (desktop) + step content */}
            <motion.main
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className={
                    leftRail
                        ? "relative z-10 mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-8 px-5 py-8 sm:py-12 md:grid-cols-[280px_1fr] md:gap-12 md:px-8"
                        : "relative z-10 mx-auto w-full max-w-[860px] px-5 py-10 sm:py-16 md:px-8"
                }
                data-testid="inquiry-main"
            >
                {leftRail}
                <div className="min-w-0">{children}</div>
            </motion.main>
        </div>
    );
};

export default InquiryShell;
